import React, { useState } from 'react';
import { Car, Plane, Crown, MapPin, ArrowRight, Send, Loader2, CheckCircle, Shield, Clock, Users } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRANSFER_TYPES = [
  { name: 'Standard Transfer', icon: <Car className="w-8 h-8" />, desc: 'Comfortable sedans for up to 3 passengers', price: 'From Rs.1,500', features: ['AC Sedan', 'Meet & greet', 'Flight tracking', 'Free waiting'] },
  { name: 'Premium Transfer', icon: <Crown className="w-8 h-8" />, desc: 'Luxury vehicles for a VIP airport experience', price: 'From Rs.4,000', features: ['Luxury vehicle', 'Professional chauffeur', 'Complimentary water', 'Premium service'] },
  { name: 'Group Transfer', icon: <Users className="w-8 h-8" />, desc: 'Spacious vans and buses for groups and families', price: 'From Rs.3,000', features: ['Minivan/Bus', 'Up to 12 passengers', 'Luggage space', 'Group discounts'] },
];

const AIRPORTS = [
  'Delhi (DEL)', 'Mumbai (BOM)', 'Bangalore (BLR)', 'Dubai (DXB)', 'Bangkok (BKK)',
  'Singapore (SIN)', 'London (LHR)', 'Paris (CDG)', 'Bali (DPS)', 'Goa (GOI)',
];

export default function AirportTransferPage() {
  const [form, setForm] = useState({ name: '', phone: '', destination: '', travel_date: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/service-inquiry`, { ...form, service_type: 'airport-transfer' });
      setSuccess(true);
      setForm({ name: '', phone: '', destination: '', travel_date: '', message: '' });
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  return (
    <div data-testid="airport-transfer-page" className="min-h-screen bg-white pt-20">
      {/* Hero */}
      <div className="relative h-[40vh] min-h-[300px] overflow-hidden">
        <img src="https://images.pexels.com/photos/4530180/pexels-photo-4530180.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Airport Transfer" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 to-navy/50" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Seamless Travel</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">Airport Transfers Made Easy</h1>
          <p className="font-body text-base text-white/80 max-w-xl">Reliable pickup and drop services at airports worldwide</p>
        </div>
      </div>

      {/* How It Works */}
      <div className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Simple Process</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy">How It Works</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-16">
            {[
              { step: '1', title: 'Share Your Details', desc: 'Tell us your flight info, pickup/drop location, and number of passengers.' },
              { step: '2', title: 'We Assign a Driver', desc: 'A professional driver will be assigned and will track your flight in real-time.' },
              { step: '3', title: 'Enjoy the Ride', desc: 'Your driver meets you at the airport with a name board. Sit back and relax.' },
            ].map(s => (
              <div key={s.step} className="text-center">
                <div className="w-14 h-14 bg-gold text-white rounded-full flex items-center justify-center mx-auto mb-4 font-heading text-xl font-bold">{s.step}</div>
                <h3 className="font-heading text-lg font-semibold text-navy mb-2">{s.title}</h3>
                <p className="font-body text-sm text-gray-500">{s.desc}</p>
              </div>
            ))}
          </div>

          {/* Transfer Types */}
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-navy">Vehicle Options</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16">
            {TRANSFER_TYPES.map(t => (
              <div key={t.name} data-testid={`transfer-type-${t.name.toLowerCase().replace(/\s/g, '-')}`}
                className="p-6 rounded-3xl bg-white border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-2 transition-transform duration-300">
                <div className="w-14 h-14 bg-gold/10 rounded-2xl flex items-center justify-center mb-4 text-gold">{t.icon}</div>
                <h3 className="font-heading text-lg font-semibold text-navy mb-1">{t.name}</h3>
                <p className="font-body text-xs text-gray-500 mb-3">{t.desc}</p>
                <p className="font-heading text-base font-bold text-gold mb-4">{t.price}</p>
                <div className="space-y-2">
                  {t.features.map(f => (
                    <div key={f} className="flex items-center gap-2 font-body text-xs text-gray-500">
                      <CheckCircle className="w-3 h-3 text-green-500" /> {f}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Popular Airports */}
          <div className="mb-12">
            <h3 className="font-heading text-xl font-bold text-navy mb-6 text-center">We Cover 100+ Airports Including</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {AIRPORTS.map(a => (
                <span key={a} className="px-4 py-2 rounded-full bg-navy-50 border border-gray-100 font-body text-sm text-navy flex items-center gap-1">
                  <Plane className="w-3 h-3 text-gold" /> {a}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Booking Form */}
      <div className="section-padding bg-navy">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Book Now</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white">Book Transfer</h2>
          </div>
          {success ? (
            <div data-testid="transfer-form-success" className="text-center p-10 rounded-3xl bg-white/10 border border-white/20">
              <CheckCircle className="w-16 h-16 text-gold mx-auto mb-4" />
              <h3 className="font-heading text-xl font-semibold text-white mb-2">Transfer Booked!</h3>
              <p className="font-body text-sm text-gray-400">We'll confirm the driver details via WhatsApp.</p>
              <button onClick={() => setSuccess(false)} className="mt-4 font-body text-sm text-gold hover:underline">Book another</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="transfer-form" className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                  placeholder="Full Name" className="px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold" />
                <input type="tel" required value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                  placeholder="Phone Number" className="px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input type="text" value={form.destination} onChange={e => setForm({...form, destination: e.target.value})}
                  placeholder="Pickup Location / Airport" className="px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold" />
                <input type="date" value={form.travel_date} onChange={e => setForm({...form, travel_date: e.target.value})}
                  className="px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold" />
              </div>
              <textarea rows={3} value={form.message} onChange={e => setForm({...form, message: e.target.value})}
                placeholder="Drop location, flight details, number of passengers..." className="w-full px-4 py-3 rounded-2xl bg-white/10 border border-white/20 font-body text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-gold resize-none" />
              <button type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-gold text-white hover:bg-gold-dark rounded-full py-3.5 font-body font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Book Transfer</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
